import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { hasAuthenticatedSession } from '../utils/checkoutAuth';
import { getTranslation } from '../utils/translationHelpers';
import './SubscriptionPage.css';

type PlanId = 'weekly' | 'monthly' | 'lifetime';
const PENDING_PLAN_KEY = 'subscription.pendingPlan';
const PENDING_PLAN_FALLBACK_KEY = 'subscription.pendingPlan.local';

const parsePendingPlan = (value: string | null): PlanId | null => {
  if (value === 'weekly' || value === 'monthly' || value === 'lifetime') {
    return value;
  }
  return null;
};

export const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const [checkoutPlan, setCheckoutPlan] = useState<PlanId | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const pendingPlanAutostartAttempted = useRef(false);
  const { t_nested, currentLanguage } = useLanguage();

  const t = (key: string, fallback: string) =>
    getTranslation(t_nested, `subscriptionPage.${key}`, fallback);

  const isRtl = currentLanguage === 'ar';
  const langAttr = currentLanguage === 'ar' ? 'ar' : currentLanguage === 'en' ? 'en' : 'nl';
  const backLabel = getTranslation(t_nested, 'aria.backToDashboard', 'Back');

  let examDate: Date | null = null;
  try {
    const stored = localStorage.getItem('EXAM_DATE_PENDING');
    if (stored) {
      const d = new Date(stored);
      examDate = Number.isNaN(d.getTime()) ? null : d;
    }
  } catch {
    examDate = null;
  }

  const daysLeft =
    examDate != null
      ? Math.ceil((examDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null;

  const showUrgency =
    daysLeft != null && Number.isFinite(daysLeft) && daysLeft > 0 && daysLeft <= 30;

  useEffect(() => {
    let active = true;
    const verifyAuth = async () => {
      const authed = await hasAuthenticatedSession();
      if (!active) return;
      setAuthChecked(authed);
    };
    void verifyAuth();
    return () => { active = false; };
  }, []);

  const startCheckout = async (planId: PlanId): Promise<boolean> => {
    setCheckoutError(null);
    setCheckoutPlan(planId);
    try {
      const { subscriptionService } = await import('../services/subscriptionService');
      const upgrade =
        planId === 'weekly'
          ? subscriptionService.upgradeToWeekly
          : planId === 'monthly'
            ? subscriptionService.upgradeToMonthly
            : subscriptionService.upgradeToLifetime;
      const result = await upgrade();
      if (!result.success && result.error) {
        setCheckoutError(result.error);
      }
      return result.success;
    } finally {
      setCheckoutPlan(null);
    }
  };

  useEffect(() => {
    if (!authChecked || checkoutPlan !== null) return;
    if (pendingPlanAutostartAttempted.current) return;
    const pendingPlan =
      parsePendingPlan(sessionStorage.getItem(PENDING_PLAN_KEY)) ||
      parsePendingPlan(localStorage.getItem(PENDING_PLAN_FALLBACK_KEY));
    if (pendingPlan) {
      pendingPlanAutostartAttempted.current = true;
      void (async () => {
        const started = await startCheckout(pendingPlan);
        if (started) {
          sessionStorage.removeItem(PENDING_PLAN_KEY);
          localStorage.removeItem(PENDING_PLAN_FALLBACK_KEY);
        }
      })();
    }
  }, [authChecked, checkoutPlan]);

  const handleSubscribe = async (planId: PlanId) => {
    await startCheckout(planId);
  };

  const PAID_PLANS = [
    {
      id: 'weekly' as PlanId,
      badge: t('badgeMostChosen', 'Meest gekozen'),
      badgeAccent: true,
      highlight: true,
      name: t('weekName', 'Week'),
      price: '€8',
      period: t('weekPeriod', 'per week'),
      cta: t('weekCta', 'Begin met 1 week'),
    },
    {
      id: 'monthly' as PlanId,
      badge: t('badgeMoreTime', 'Meer tijd'),
      badgeAccent: false,
      highlight: false,
      name: t('monthName', 'Maand'),
      price: '€20',
      period: t('monthPeriod', 'per maand'),
      cta: t('monthCta', 'Start maand'),
    },
    {
      id: 'lifetime' as PlanId,
      badge: t('badgeBestValue', 'Beste waarde'),
      badgeAccent: false,
      highlight: false,
      name: t('lifetimeName', 'Lifetime'),
      price: '€60',
      period: t('lifetimePeriod', 'eenmalig'),
      cta: t('lifetimeCta', 'Koop lifetime'),
    },
  ];

  const FEATURES = [
    t('feature1', 'Onbeperkt oefenexamens'),
    t('feature2', 'Focus op jouw zwakke punten'),
    t('feature3', 'Persoonlijk CBR-examen'),
    t('feature4', 'WhatsApp oefenen'),
    t('feature5', '3 talen beschikbaar'),
  ];

  return (
    <div className="sub-page safe-top" lang={langAttr} dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Back button */}
      <div className="sub-top-bar">
        <button
          type="button"
          className="sub-back-btn"
          onClick={() => navigate(-1)}
          aria-label={backLabel}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </div>

      <div className="sub-content">

        {/* Social proof pill */}
        <div className="sub-social-proof" role="status">
          🎯 {t('socialProofNew', 'Meer dan 300+ CBR-kandidaten gaan je voor')}
        </div>

        {/* Hero */}
        <div className="sub-header">
          {showUrgency && daysLeft != null && (
            <div className="sub-urgency" role="status">
              ⏰ {t('urgencyBanner', 'Examen over {days} dagen — dit is het moment om door te gaan').replace('{days}', String(daysLeft))}
            </div>
          )}
          <h1 className="sub-headline">
            {t('heroTitle', 'Sla je theorie-examen')}
            <span className="sub-headline__accent"><br />{t('heroAccentNew', 'stop zodra je slaagt')}</span>
          </h1>
        </div>

        {/* Section title */}
        <p className="sub-choose-plan">{t('choosePlanTitle', 'Kies jouw plan')}</p>

        {/* Error */}
        {checkoutError && (
          <p className="sub-checkout-error" role="alert">{checkoutError}</p>
        )}

        {/* Plans */}
        <div className="sub-plans">
          {PAID_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`sub-plan${plan.highlight ? ' sub-plan--highlight' : ''}`}
            >
              {plan.badge && (
                <div className={`sub-plan__badge${plan.badgeAccent ? ' sub-plan__badge--accent' : ''}`}>
                  {plan.badge}
                </div>
              )}
              <div className="sub-plan__name">{plan.name}</div>
              <div className="sub-plan__pricing">
                <span className="sub-plan__price">{plan.price}</span>
                <span className="sub-plan__period">{plan.period}</span>
              </div>
              <ul
                className="sub-plan__features"
                aria-label={getTranslation(t_nested, 'subscriptionPage.featuresListAria', 'Included')}
              >
                {FEATURES.map((f) => (
                  <li key={f}>
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3 8l3.5 3.5L13 4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={plan.highlight ? 'sub-plan__cta sub-plan__cta--primary' : 'sub-plan__cta'}
                disabled={checkoutPlan !== null}
                onClick={() => handleSubscribe(plan.id)}
              >
                {checkoutPlan === plan.id ? '…' : plan.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Objections */}
        <div className="sub-objections">
          <div className="sub-objection">
            <span className="sub-objection__check">✓</span>
            <span>{t('objection1', 'Goedkoper dan een herexamen (€50+)')}</span>
          </div>
          <div className="sub-objection">
            <span className="sub-objection__check">✓</span>
            <span>{t('objection2', 'Stop wanneer je wilt — geen verplichtingen')}</span>
          </div>
        </div>

      </div>

      {/* Stripe trust */}
      <div className="sub-stripe-trust" role="contentinfo">
        <svg className="sub-stripe-trust__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M10 1.5a4 4 0 0 0-4 4V8H5a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 5 18h10a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 15 8h-1V5.5a4 4 0 0 0-4-4Zm-2.5 4a2.5 2.5 0 0 1 5 0V8h-5V5.5Z" />
        </svg>
        <span>{t('stripeTrustLead', 'Veilig afrekenen via')} <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" aria-label={t('stripePoweredByAria', 'Stripe')}>Stripe</a></span>
        <span className="sub-stripe-trust__sep" aria-hidden>·</span>
        <span>{t('stripeTrustCancel', 'Opzeggen kan altijd')}</span>
        <span className="sub-stripe-trust__sep" aria-hidden>·</span>
        <span>{t('stripeTrustEncryption', '256-bit SSL')}</span>
      </div>
    </div>
  );
};
